import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { DatePicker } from '@mui/x-date-pickers';

import GridPagination from '~/components/Common/Elements/GridPagination';

import { useGetToursPricingHistory } from '~/hooks/apis/lere/useGetToursPricingHistory';
import useDataGridPagination from '~/hooks/mui/useDataGridPagination';

import { Dayjs } from '~/services/TimeService';

import ToursIdsFormatter from './formatters/ToursIdsFormatter';
import ToursPricingFormatter from './formatters/ToursPricingFormatter';
import {
  SUGGESTED_ACTION_TYPE_OPTIONS,
  capitalizeFirst,
  formatDate,
  formatDateTime,
  getStatusColor,
  getTourOfferHref,
} from './formatters/toursPricingUtils';
import {
  LerePaginatedResponse,
  LereTabComponentProps,
  LereToursPricingHistoryParams,
  LereToursPricingHistoryRecord,
  SuggestedActionType,
  ToursPricingHistoryStatus,
} from './types';

const EMPTY_RESPONSE: LerePaginatedResponse<LereToursPricingHistoryRecord> = {
  result: [],
  total: 0,
};

const STATUS_OPTIONS: Array<{ value: ToursPricingHistoryStatus; label: string }> = [
  { value: 'accepted', label: 'Accepted' },
  { value: 'rejected', label: 'Rejected' },
];

type AppliedFilters = Omit<LereToursPricingHistoryParams, 'page' | 'limit'>;

export default function RMToursPricingChangeLog({ onLereError }: LereTabComponentProps) {
  const { paginationModel, setPaginationModel, setPage } = useDataGridPagination({ page: 0, pageSize: 20 });

  const [offerBkInput, setOfferBkInput] = useState('');
  const [salesforceIdInput, setSalesforceIdInput] = useState('');
  const [actionTypeInput, setActionTypeInput] = useState<SuggestedActionType | ''>('');
  const [statusInput, setStatusInput] = useState<ToursPricingHistoryStatus | ''>('');
  const [startDateInput, setStartDateInput] = useState<Dayjs | null>(null);
  const [endDateInput, setEndDateInput] = useState<Dayjs | null>(null);

  const [appliedFilters, setAppliedFilters] = useState<AppliedFilters>({});

  const queryParams = useMemo<LereToursPricingHistoryParams>(
    () => ({
      page: paginationModel.page + 1,
      limit: paginationModel.pageSize,
      ...appliedFilters,
    }),
    [paginationModel.page, paginationModel.pageSize, appliedFilters],
  );

  const historyQuery = useGetToursPricingHistory(queryParams);

  const historyData = historyQuery.data ?? EMPTY_RESPONSE;

  useEffect(() => {
    if (historyQuery.error) {
      onLereError(historyQuery.error);
    }
  }, [historyQuery.error, onLereError]);

  const handleSearch = useCallback(() => {
    setAppliedFilters({
      offer_bk: offerBkInput || undefined,
      offer_salesforce_bk: salesforceIdInput || undefined,
      suggested_action_type: actionTypeInput || undefined,
      status: statusInput || undefined,
      created_at_start: startDateInput?.startOf('day').toISOString() || undefined,
      created_at_end: endDateInput?.endOf('day').toISOString() || undefined,
    });
    setPage(0);
  }, [offerBkInput, salesforceIdInput, actionTypeInput, statusInput, startDateInput, endDateInput, setPage]);

  const handleClearFilters = () => {
    setOfferBkInput('');
    setSalesforceIdInput('');
    setActionTypeInput('');
    setStatusInput('');
    setStartDateInput(null);
    setEndDateInput(null);
    setAppliedFilters({});
    setPage(0);
  };

  const dateRangeError =
    startDateInput && endDateInput && endDateInput.isBefore(startDateInput, 'day')
      ? "'Actioned To' must not be before 'Actioned From'"
      : '';

  const handleActionTypeChange = (event: SelectChangeEvent) => {
    setActionTypeInput(event.target.value as SuggestedActionType | '');
  };

  const handleStatusChange = (event: SelectChangeEvent) => {
    setStatusInput(event.target.value as ToursPricingHistoryStatus | '');
  };

  const columns = useMemo<Array<GridColDef<LereToursPricingHistoryRecord>>>(() => {
    const compactCol: Partial<GridColDef<LereToursPricingHistoryRecord>> = {
      minWidth: 120,
      flex: 1,
    };

    return [
      {
        ...compactCol,
        field: 'offer_name',
        headerName: 'Offer Name',
        minWidth: 220,
        flex: 2,
        renderCell: ({ row }) => {
          const name = row.offer_name || '—';
          const offerHref = getTourOfferHref(row.offer_bk, row.offer_url);
          if (offerHref && name !== '—') {
            return (
              <Link href={offerHref} target="_blank" rel="noopener noreferrer" underline="hover">
                {name}
              </Link>
            );
          }
          return name;
        },
      },
      {
        field: 'ids',
        headerName: 'IDs',
        display: 'flex',
        flex: 2,
        minWidth: 180,
        sortable: false,
        filterable: false,
        renderCell: ToursIdsFormatter,
      },
      {
        ...compactCol,
        field: 'suggested_action_type',
        headerName: 'Action Type',
        renderCell: ({ row }) => capitalizeFirst(row.suggested_action_type),
      },
      {
        ...compactCol,
        field: 'departure_date',
        headerName: 'Departure Date',
        minWidth: 135,
        renderCell: ({ row }) => formatDate(row.departure_date),
      },
      {
        field: 'pricing',
        headerName: 'Pricing',
        display: 'flex',
        flex: 2,
        minWidth: 180,
        sortable: false,
        filterable: false,
        renderCell: ToursPricingFormatter,
      },
      {
        field: 'capacity',
        headerName: 'Capacity',
        display: 'flex',
        flex: 1,
        minWidth: 120,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => (
          <Box sx={{ alignSelf: 'flex-start', py: 1 }}>
            <Typography variant="body2">
              <Typography component="span" fontWeight="bold" variant="body2">
                Dep Pax Sold / Max:
              </Typography>{' '}
              {row.total_pax_sold.toLocaleString()} / {row.max_pax.toLocaleString()}
            </Typography>
            <Typography variant="body2">
              <Typography component="span" fontWeight="bold" variant="body2">
                Leadtime:
              </Typography>{' '}
              {row.leadtime.toLocaleString()}d
            </Typography>
          </Box>
        ),
      },
      {
        field: 'status',
        headerName: 'Status',
        minWidth: 110,
        flex: 1,
        renderCell: ({ row }) => (
          <Chip label={row.status.toUpperCase()} size="small" color={getStatusColor(row.status)} />
        ),
      },
      {
        field: 'actioned_by',
        headerName: 'Actioned By',
        display: 'flex',
        flex: 1.5,
        minWidth: 150,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => (
          <Box sx={{ alignSelf: 'flex-start', py: 1 }}>
            {row.created_by_user_id && row.created_by_username ? (
              <Link
                href={`/users/${row.created_by_user_id}`}
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
              >
                {row.created_by_username}
              </Link>
            ) : (
              <Typography variant="body2">—</Typography>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {formatDateTime(row.created_at)}
            </Typography>
          </Box>
        ),
      },
    ];
  }, []);

  return (
    <Box>
      <Typography variant="h2" fontSize="2em" fontWeight="medium">
        RM Tours Change Log
      </Typography>
      <Typography variant="body1" sx={{ mt: 1 }}>
        History of accepted and rejected tours dynamic pricing recommendations.
      </Typography>
      <Typography variant="subtitle1" fontWeight="medium" sx={{ mt: 3 }}>
        Filters
      </Typography>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1 }}>
        <TextField
          label="Offer ID"
          size="small"
          value={offerBkInput}
          onChange={(e) => setOfferBkInput(e.target.value)}
          sx={{ minWidth: 200 }}
        />
        <TextField
          label="Salesforce ID"
          size="small"
          value={salesforceIdInput}
          onChange={(e) => setSalesforceIdInput(e.target.value)}
          sx={{ minWidth: 200 }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Action Type</InputLabel>
          <Select value={actionTypeInput} label="Action Type" onChange={handleActionTypeChange}>
            <MenuItem value="">All</MenuItem>
            {SUGGESTED_ACTION_TYPE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusInput} label="Status" onChange={handleStatusChange}>
            <MenuItem value="">All</MenuItem>
            {STATUS_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 2, mb: 3 }}>
        <DatePicker
          label="Actioned From"
          value={startDateInput}
          onChange={setStartDateInput}
          slotProps={{ textField: { size: 'small', sx: { minWidth: 180 } } }}
        />
        <DatePicker
          label="Actioned To"
          value={endDateInput}
          onChange={setEndDateInput}
          slotProps={{
            textField: {
              size: 'small',
              sx: { minWidth: 180 },
              error: !!dateRangeError,
              helperText: dateRangeError,
            },
          }}
        />
        <Button variant="text" disabled={historyQuery.isFetching} onClick={handleClearFilters}>
          Clear filters
        </Button>
        <Button variant="contained" disabled={historyQuery.isFetching || !!dateRangeError} onClick={handleSearch}>
          Search
        </Button>
      </Stack>

      <DataGrid
        autoHeight
        columns={columns}
        rows={historyData.result}
        rowCount={historyData.total}
        loading={historyQuery.isFetching}
        pagination
        paginationMode="server"
        pageSizeOptions={[10, 20, 50, 100]}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        getRowHeight={() => 'auto'}
        getRowId={(row) => row.id}
        slots={{
          pagination: GridPagination,
        }}
        disableRowSelectionOnClick
        disableColumnMenu
        sx={{
          width: '100%',
          '& .MuiDataGrid-row': {
            cursor: 'default',
          },
          '& .MuiDataGrid-row:hover': {
            backgroundColor: 'transparent',
          },
        }}
      />
    </Box>
  );
}
